import { useState } from "react";
import { t } from "i18next";

import { IFooter, ISocial, ISocialItens, SocialIconType } from "../../../interfaces/event/footer"

import BaseToggle from "../../shared/BaseToggle";
import { FacebookRound } from "../../shared/Icons/FacebookRound";
import { FacebookSquare } from "../../shared/Icons/FacebookSquare";
import { FacebookSquareRounded } from "../../shared/Icons/FacebookSquareRounded";
import Input from "../../shared/input/Input";
import InputColorPicker from "../../shared/inputColorPicker/InputColorPicker";
import PageTitle from "../../shared/pageTitle/PageTitle";

import { Label } from "../../shared-v2/Label";
import { Subtitle } from "../../shared-v2/Subtitle";
import { IFile, UploadImage } from "../../shared-v2/UploadImage";

import { Column, InputGroup, Row, SocialIconsContainer} from "./style";
import { breakpointSize } from "../../../theme/mediaQuerie";
import { SelectButton } from "../../shared-v2/SelectButton";

interface IProps {
  footer: IFooter;
  setFooter: React.Dispatch<React.SetStateAction<IFooter>>;
}

const isMobile = window.innerWidth <= breakpointSize.Sm;

export const Footer = ({footer, setFooter}: IProps) => {
  const [file, setFile] = useState<IFile | null>(null);

  const updateFooter = (attr: keyof IFooter, value: string) => {
    let newFooter = {...footer, [attr]: value};
    setFooter(prev => ({...prev, [attr]: value}));
  }

  const updateSocial = (attr: keyof ISocial, value: string) => {
    setFooter(prev => ({...prev, social: {...prev.social, [attr]: value}}));
  }

  const updateSocialItens = (attr: keyof ISocialItens, social: SocialIconType, value: string | boolean) => {
    setFooter(prev => ({...prev, social: {...prev.social, itens: {...prev.social.itens, 
      [social]: {...prev.social.itens[social], [attr]: value}
    }}}));
  }

  return (
    <>
      {!isMobile && <PageTitle style={{margin: 0}}>{t("footer")}</PageTitle>}

      <Row>
        <Column>
          <Subtitle>{t("general")}</Subtitle>
          <Row>
            <Column>
              <Label>{t("backgroundColor")}</Label>
              <InputColorPicker 
                color={footer.background}  
                handleChangeColor={(e) => updateFooter("background", e.hex)}
              />
            </Column>
            <Column>
              <Label>{t("textColor")}</Label>
              <InputColorPicker 
                color={footer.color}  
                handleChangeColor={(e) => updateFooter("color", e.hex)}
              />
            </Column>
          </Row>
        </Column>

        <UploadImage
          file={file}
          setFile={(file) => {updateFooter("logo", file?.url ?? ""); setFile(file);}}
          id="footer-upload-image"
          title="Logo"
          maxImageSize={1024 * 1024 * 10}
        />
      </Row>

      <Row>
        <Column>
          <Subtitle>{t("dashboard.event.footer.iconsStyle")}</Subtitle>
          <Row>
            <Column>
              <Label>{t("backgroundColor")}</Label>
              <InputColorPicker 
                color={footer.social.background}  
                handleChangeColor={(e) => updateSocial("background", e.hex)}
              />
            </Column>
            <Column>
              <Label>{t("iconColor")}</Label>
              <InputColorPicker
                color={footer.social.color}  
                handleChangeColor={(e) => updateSocial("color", e.hex)}
              />
            </Column>
          </Row>
        </Column>
        <SocialIconsContainer>
          <SelectButton 
            selected={footer.social.format === "round"}
            onClick={() => updateSocial("format", "round")}
          > 
            <FacebookRound /> 
          </SelectButton>
          <SelectButton 
            selected={footer.social.format === "square"}
            onClick={() => updateSocial("format", "square")}
          >  
            <FacebookSquare /> 
          </SelectButton>
          <SelectButton 
            selected={footer.social.format === "squareRounded"}
            onClick={() => updateSocial("format", "squareRounded")}
          >  
            <FacebookSquareRounded /> 
          </SelectButton>
        </SocialIconsContainer>
      </Row>

      <Column>
        <InputGroup>
          <Subtitle>Facebook</Subtitle>
          <BaseToggle 
            initialCheck={footer.social.itens.facebook.active ?? false} 
            onChange={(checked: boolean) => updateSocialItens("active", "facebook", checked)} 
          />
        </InputGroup>
        <Input 
          placeholder="https://" 
          value={footer.social.itens.facebook.url}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
            updateSocialItens("url", "facebook", e.target.value)} 
        />

        <InputGroup>
          <Subtitle>Instagram</Subtitle>
          <BaseToggle 
            initialCheck={footer.social.itens.instagram.active ?? false} 
            onChange={(checked: boolean) => updateSocialItens("active", "instagram", checked)} 
          />
        </InputGroup>
        <Input 
          placeholder="https://" 
          value={footer.social.itens.instagram.url} 
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
            updateSocialItens("url", "instagram", e.target.value)} 
        />

        <InputGroup>
          <Subtitle>X</Subtitle>
          <BaseToggle 
            initialCheck={footer.social.itens.X.active ?? false} 
            onChange={(checked: boolean) => updateSocialItens("active", "X", checked)} 
          />
        </InputGroup>
        <Input 
          placeholder="https://" 
          value={footer.social.itens.X.url} 
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
            updateSocialItens("url", "X", e.target.value)} 
        />

        <InputGroup>
          <Subtitle>Youtube</Subtitle>
          <BaseToggle 
            initialCheck={footer.social.itens.youtube.active ?? false} 
            onChange={(checked: boolean) => updateSocialItens("active", "youtube", checked)} 
          />
        </InputGroup>
        <Input 
          placeholder="https://" 
          value={footer.social.itens.youtube.url} 
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
            updateSocialItens("url", "youtube", e.target.value)} 
        />

        <InputGroup>
          <Subtitle>LinkedIn</Subtitle>
          <BaseToggle 
            initialCheck={footer.social.itens.linkedin.active ?? false} 
            onChange={(checked: boolean) => updateSocialItens("active", "linkedin", checked)} 
          />
        </InputGroup>
        <Input 
          placeholder="https://" 
          value={footer.social.itens.linkedin.url} 
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
            updateSocialItens("url", "linkedin", e.target.value)} 
        />

        <InputGroup>
          <Subtitle>TikTok</Subtitle>
          <BaseToggle 
            initialCheck={footer.social.itens.tiktok.active ?? false} 
            onChange={(checked: boolean) => updateSocialItens("active", "tiktok", checked)} 
          />
        </InputGroup>
        <Input 
          placeholder="https://" 
          value={footer.social.itens.tiktok.url} 
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
            updateSocialItens("url", "tiktok", e.target.value)} 
        />

        <InputGroup>
          <Subtitle>Threads</Subtitle>
          <BaseToggle 
            initialCheck={footer.social.itens.threads.active ?? false} 
            onChange={(checked: boolean) => updateSocialItens("active", "threads", checked)} 
          />
        </InputGroup>
        <Input 
          placeholder="https://" 
          value={footer.social.itens.threads.url} 
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
            updateSocialItens("url", "threads", e.target.value)} 
        />

      </Column>
    </>
  );
}